"use client"

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"

export type MenuState = "full" | "collapsed" | "hidden"

interface SidebarContextValue {
  menuState: MenuState
  isHovered: boolean
  isMobile: boolean
  isMobileOpen: boolean
  lastToggleAt: number | null
  setMenuState: (state: MenuState) => void
  setIsHovered: (hovered: boolean) => void
  setIsMobileOpen: (open: boolean) => void
  toggleMenuState: () => void
  toggleMobileOpen: () => void
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined)

const STORAGE_KEY = "promobot_sidebar_state"

function readPersistedState(): MenuState {
  if (typeof window === "undefined") return "full"
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (raw === "full" || raw === "collapsed" || raw === "hidden") return raw
  return "full"
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [menuState, setMenuStateRaw] = useState<MenuState>("full")
  const [previousDesktopState, setPreviousDesktopState] = useState<MenuState>("full")
  const [isHovered, setIsHovered] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [lastToggleAt, setLastToggleAt] = useState<number | null>(null)

  useEffect(() => {
    const persisted = readPersistedState()
    setMenuStateRaw(persisted)
    setPreviousDesktopState(persisted === "hidden" ? "full" : persisted)
  }, [])

  const setMenuState = useCallback((state: MenuState) => {
    setMenuStateRaw(state)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, state)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024
      setIsMobile(!desktop)
      if (!desktop) {
        setMenuStateRaw((prev) => {
          if (prev !== "hidden") setPreviousDesktopState(prev)
          return "hidden"
        })
      } else {
        setMenuStateRaw((prev) => {
          if (prev === "hidden" && previousDesktopState !== "hidden") return previousDesktopState
          return prev
        })
      }
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [previousDesktopState])

  const toggleMenuState = useCallback(() => {
    setMenuStateRaw((prev) => {
      const next: MenuState = prev === "full" ? "collapsed" : prev === "collapsed" ? "hidden" : "full"
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, next)
      }
      return next
    })
    setLastToggleAt(Date.now())
  }, [])

  const toggleMobileOpen = useCallback(() => {
    setIsMobileOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isEditable =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      if (isEditable) return
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault()
        toggleMenuState()
      }
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [toggleMenuState])

  return (
    <SidebarContext.Provider
      value={{
        menuState,
        isHovered,
        isMobile,
        isMobileOpen,
        lastToggleAt,
        setMenuState,
        setIsHovered,
        setIsMobileOpen,
        toggleMenuState,
        toggleMobileOpen,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const ctx = useContext(SidebarContext)
  if (!ctx) {
    throw new Error("useSidebar must be used within SidebarProvider")
  }
  return ctx
}
